import { useCallback, useEffect, useState } from "react";
import { ChapterView } from "@/components/content/ChapterView";
import { GlobalHeader } from "@/components/shell/GlobalHeader";
import { SideRail } from "@/components/shell/SideRail";
import { getChapter } from "@/content/chapters";
import { useHashRoute } from "@/routing/useHashRoute";
import "@/styles/tokens.css";
import "@/styles/glass.css";
import "@/styles/shell.css";
import "@/styles/chapters.css";
import "@/styles/prototype-routes.css";
import "@/styles/botanical-veil.css";

export default function App() {
  const { route } = useHashRoute();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [railOpen, setRailOpen] = useState(false);
  const [activeAnchor, setActiveAnchor] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);

  const railEntries = getChapter(route).railEntries;

  const onNavigateAnchor = useCallback((anchor: string) => {
    const el = document.getElementById(anchor);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setActiveAnchor(anchor);
    }
  }, []);

  useEffect(() => {
    setMobileNavOpen(false);
    setRailOpen(false);
    setActiveAnchor(railEntries[0]?.anchor ?? null);
  }, [route, railEntries]);

  useEffect(() => {
    if (railEntries.length === 0) return;
    const observer = new IntersectionObserver(
      (records) => {
        const visible = records
          .filter((r) => r.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]?.target.id) {
          setActiveAnchor(visible[0].target.id);
        }
      },
      { rootMargin: "-15% 0px -60% 0px", threshold: [0, 0.2, 0.5] },
    );
    for (const entry of railEntries) {
      const el = document.getElementById(entry.anchor);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, [route, railEntries]);

  useEffect(() => {
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const pct = max > 0 ? (window.scrollY / max) * 100 : 0;
      setProgress(Math.min(100, Math.max(0, pct)));
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setRailOpen(false);
        setMobileNavOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    document.getElementById("content")?.focus({ preventScroll: true });
  }, [route]);

  return (
    <div className="app-shell">
      <a className="skip-link" href="#content">
        Skip to content
      </a>
      <div className="progress" style={{ width: `${progress}%` }} aria-hidden="true" />

      <div className="observatory">
        <GlobalHeader
          mobileNavOpen={mobileNavOpen}
          onToggleMobileNav={() => setMobileNavOpen((v) => !v)}
        />

        <div className="observatory-body">
          <SideRail
            route={route}
            entries={railEntries}
            activeAnchor={activeAnchor}
            open={railOpen}
            onToggle={() => setRailOpen((v) => !v)}
            onClose={() => setRailOpen(false)}
            onNavigateAnchor={onNavigateAnchor}
          />

          <main id="content" className="shell-main" tabIndex={-1} data-route={route}>
            <ChapterView route={route} />
          </main>
        </div>

        <footer className="page-footer">
          <span>Living research · synthesis v0.1</span>
          <span>Content changes; the evidence remains visible.</span>
        </footer>
      </div>
    </div>
  );
}
